import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../lib/api';

interface EVEAccount {
  id: string;
  characterId: string;
  characterName: string;
  corporationId: string | null;
  corporationName: string | null;
  allianceId: string | null;
  allianceName: string | null;
  scopes: string | null;
  includeCorporation: boolean;
}

interface PPSAccount {
  id: string;
  mainCharacterId: string | null;
  eveAccounts: EVEAccount[];
  mainCharacter: EVEAccount | null;
}

interface AuthState {
  sessionToken: string | null;
  ppsAccount: PPSAccount | null;
  currentCharacterId: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: AuthState = {
  sessionToken: null,
  ppsAccount: null,
  currentCharacterId: null,
  loading: false,
  error: null,
  initialized: false,
};

export const fetchSession = createAsyncThunk('auth/fetchSession', async (token: string) => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  const response = await api.get('/auth/session');
  return { token, ...response.data };
});

export const logout = createAsyncThunk('auth/logout', async () => {
  await api.post('/auth/logout');
  localStorage.removeItem('sessionToken');
  delete api.defaults.headers.common['Authorization'];
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSession: (state, action: PayloadAction<string>) => {
      state.sessionToken = action.payload;
      api.defaults.headers.common['Authorization'] = `Bearer ${action.payload}`;
    },
    clearSession: (state) => {
      state.sessionToken = null;
      state.ppsAccount = null;
      state.currentCharacterId = null;
      state.initialized = false;
      localStorage.removeItem('sessionToken');
      delete api.defaults.headers.common['Authorization'];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionToken = action.payload.token;
        state.ppsAccount = action.payload.ppsAccount;
        state.currentCharacterId = action.payload.currentCharacterId;
        state.initialized = true;
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch session';
        state.sessionToken = null;
        state.ppsAccount = null;
        state.currentCharacterId = null;
        state.initialized = true;
        localStorage.removeItem('sessionToken');
      })
      .addCase(logout.fulfilled, (state) => {
        state.sessionToken = null;
        state.ppsAccount = null;
        state.currentCharacterId = null;
        state.initialized = false;
      });
  },
});

export const { setSession, clearSession } = authSlice.actions;
export default authSlice.reducer;
